/**
 * Request manager for deduplicating and managing active requests
 * Prevents duplicate requests for the same key while a request is in progress
 */
class RequestManager {
  private activeRequests: Map<string, Promise<any>> = new Map();

  /**
   * Execute a request with deduplication
   * @param key Unique key for the request (e.g., 'sendOtp:+79123456789')
   * @param requestFn Function that returns a promise (the actual request)
   * @param force If true, force a new request even if one is already in progress
   * @returns Promise from the request
   */
  async execute<T>(
    key: string,
    requestFn: () => Promise<T>,
    force: boolean = false
  ): Promise<T> {
    // If a request is already in progress and we're not forcing a new one
    if (!force && this.activeRequests.has(key)) {
      console.log(`Request ${key} already in progress, returning existing promise`);
      return this.activeRequests.get(key) as Promise<T>;
    }

    // If forcing and a request exists, remove it from tracking (but let it continue)
    if (force && this.activeRequests.has(key)) {
      console.log(`Force new request for ${key}, removing previous from tracking`);
      this.activeRequests.delete(key);
    }

    // Create new request promise
    const requestPromise = requestFn();

    // Store the promise before adding handlers
    this.activeRequests.set(key, requestPromise);

    // Add cleanup handlers that only remove if this is still the current promise
    requestPromise
      .then((result) => {
        // Only remove if this promise is still the one being tracked
        if (this.activeRequests.get(key) === requestPromise) {
          this.activeRequests.delete(key);
        }
        return result;
      })
      .catch((error) => {
        // Only remove if this promise is still the one being tracked
        if (this.activeRequests.get(key) === requestPromise) {
          this.activeRequests.delete(key);
        }
        throw error;
      });

    return requestPromise;
  }

  /**
   * Check if a request with the given key is currently in progress
   */
  isRequestInProgress(key: string): boolean {
    return this.activeRequests.has(key);
  }

  /**
   * Cancel/remove a request from active requests
   * Note: This doesn't actually cancel the Promise, just removes it from tracking
   */
  cancelRequest(key: string): void {
    this.activeRequests.delete(key);
  }


}

// Export singleton instance
export const requestManager = new RequestManager();